import { DirectedTriple, ReferenceDirection } from '@ardoq/api-types';
import { List } from './atoms';
import { OutgoingTripleOption, IncomingTripleOption } from './TripleOption';
import { InfoNotification } from '@ardoq/status-ui';
import { TripleOptions } from 'viewpointBuilder/types';
import { t, type I18nLocale } from '@ardoq/i18n';

export type TripleOptionsProps = {
  options: TripleOptions['outgoing'];
  hasTripleOptions: boolean;
  direction: ReferenceDirection;
  selection?: Record<string, boolean>;
  toggleOption: (
    tripleId: string,
    direction: ReferenceDirection,
    namedDirectedTriple: DirectedTriple,
    isSelected: boolean
  ) => void;
  selectedComponent: TripleOptions['selectedNode'];
  isDisabledWholeRow: boolean;
  i18nLocale: I18nLocale;
};

export const TripleOptionsList = ({
  options,
  direction,
  toggleOption,
  selectedComponent: { metaDataComponent: metaDataComponentSelected },
  hasTripleOptions,
  isDisabledWholeRow,
  i18nLocale,
}: TripleOptionsProps) => {
  const isOutgoing = direction === ReferenceDirection.OUTGOING;
  if (!hasTripleOptions) {
    return (
      <InfoNotification>
        {isOutgoing
          ? t(
              i18nLocale,
              'viewpointBuilder:tripleOptionsList.noOutgoing',
              'There are no outgoing references for the selected component type'
            )
          : t(
              i18nLocale,
              'viewpointBuilder:tripleOptionsList.noIncoming',
              'There are no incoming references for the selected component type'
            )}
      </InfoNotification>
    );
  }

  return (
    <List
      aria-label={
        isOutgoing
          ? t(
              i18nLocale,
              'viewpointBuilder:tripleOptionsList.outgoingOptionsAriaLabel',
              'Outgoing reference options'
            )
          : t(
              i18nLocale,
              'viewpointBuilder:tripleOptionsList.incomingOptionsAriaLabel',
              'Incoming reference options'
            )
      }
    >
      {options.map(
        ({
          tripleId,
          metaDataComponent,
          metaDataReference,
          isSelected,
          namedDirectedTriple,
          instanceCounts,
          isDisabled,
          togglingDisabledExplanation,
        }) =>
          isOutgoing ? (
            <OutgoingTripleOption
              isDisabledWholeRow={isDisabledWholeRow}
              key={tripleId}
              tripleId={tripleId}
              metaDataComponent={metaDataComponent}
              metaDataReference={metaDataReference}
              isSelected={isSelected}
              isDisabled={isDisabled}
              togglingDisabledExplanation={togglingDisabledExplanation}
              direction={direction}
              toggleOption={toggleOption}
              metaDataComponentSelected={metaDataComponentSelected}
              namedDirectedTriple={namedDirectedTriple}
              instanceCounts={instanceCounts}
              i18nLocale={i18nLocale}
            />
          ) : (
            <IncomingTripleOption
              isDisabledWholeRow={isDisabledWholeRow}
              key={tripleId}
              tripleId={tripleId}
              metaDataComponent={metaDataComponent}
              metaDataReference={metaDataReference}
              isSelected={isSelected}
              isDisabled={isDisabled}
              togglingDisabledExplanation={togglingDisabledExplanation}
              direction={direction}
              toggleOption={toggleOption}
              metaDataComponentSelected={metaDataComponentSelected}
              namedDirectedTriple={namedDirectedTriple}
              instanceCounts={instanceCounts}
              i18nLocale={i18nLocale}
            />
          )
      )}
    </List>
  );
};
